import loadable from "@loadable/component";
import { isLeft } from "fp-ts/lib/Either";
import { inject } from "mobx-react";
import { useMemo } from "react";

import { ShareButtons } from "../../../../core/components/integrations/ShareButtons.component";
import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import { HtmlEmbed } from "../../../../core/content-blocks/HtmlEmbed.component";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { generateImageAssetSizes } from "../../../../core/lib/image";
import { placeholderImage } from "@ihr-radioedit/inferno-core";
import { formatImage } from "@ihr-radioedit/inferno-core";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { zonedTimeFormat } from "../../../../core/lib/time";
import { Container, Heading } from "../../../../core/ui";
import { CoastLinkedGuests } from "../../components/CoastLinkedGuests.component";
import { CoastLinkedHost } from "../../components/CoastLinkedHost.component";
import { getCoastShowType } from "../../lib/coastContent";
import { CoastContentBlock } from "./CoastContent.component";
import { CoastShowPayloadResolver } from "./CoastContent.types";
import { CoastContentShowBumperMusic } from "./CoastContentShowBumperMusic.component";
import { RESPONSIVE_IMAGE } from "../../shared/constants";
import type { Store } from "../../../../../client/stores";

const CoastShowAudioBoxComponent = loadable(() => import("./coast-show-audiobox/CoastShowAudioBox.component"));

const log = ILog.logger("CoastContentShow");

export type CoastContentShowProps = {
    content: Webapi.PublishRecordFragment;
    eyebrow?: Webapi.Eyebrow;
    store?: Store;
};

export const CoastContentShow = inject("store")(({ content, store }: CoastContentShowProps) => {
    if (!store) {
        return null;
    }

    const { site, request } = store;
    const { env } = store;

    const resolvedPayload = CoastShowPayloadResolver.decode(content.payload);

    if (isLeft(resolvedPayload)) {
        log.error(`Payload failed validation: ${getPaths(resolvedPayload)}`);
        return null;
    }

    const { summary, fields } = resolvedPayload.right;

    const { srcset, sizes } = generateImageAssetSizes(formatImage(summary.image.url, env.IMAGE_HOST));
    const placeholderSrc = `${placeholderImage}?ops=resize(900,506),quality(10)`;

    const timezone = store.site.index.timeZone || env.DEFAULT_TIMEZONE;

    const formattedShowDate =
        fields.show_date?.value &&
        zonedTimeFormat({ date: fields.show_date.value, timezone, outputFormat: "EEEE - MMMM d yyyy" });

    const url = site.getShareUrl(request);

    const showType = useMemo(() => getCoastShowType(content), [content]);

    return (
        <Container className="component-coast-content-show coast-content-layout-container">
            <Heading level={1}>{content.summary.title}</Heading>

            <ShareButtons
                url={url}
                title={content.summary.title}
                desc={content.summary.description}
                setStyles={false}
            />

            <figure className="item-thumb">
                <section className="image-container">
                    <ResponsiveImage
                        alt={summary.image.alt_text || summary.title || ""}
                        src={placeholderSrc}
                        placeholder={placeholderSrc}
                        srcset={srcset}
                        sizes={sizes}
                        initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
                        initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
                    />
                </section>

                <figcaption>
                    {formattedShowDate ? (
                        <p>
                            <span className="thumb-meta-header">Date</span>
                            <time className="thumb-meta-value">{formattedShowDate}</time>
                        </p>
                    ) : null}
                    <CoastLinkedHost titleOnSameLine={false} item={content} />
                    <CoastLinkedGuests titleOnSameLine={false} item={content} />
                </figcaption>
            </figure>

            {fields.show_date?.value ? (
                <CoastShowAudioBoxComponent showType={showType} show={fields} showDate={fields.show_date?.value} />
            ) : null}

            <article>
                {fields.blocks.value?.map((block, i) => (
                    <CoastContentBlock block={block} key={i} />
                ))}

                {fields.websites?.value ? (
                    <section>
                        <h3>Website(s):</h3>
                        <HtmlEmbed {...fields.websites} />
                    </section>
                ) : null}

                {fields.books?.value ? (
                    <section>
                        <h3>Book(s):</h3>
                        <HtmlEmbed {...fields.books} />
                    </section>
                ) : null}

                {fields.videos?.value ? (
                    <section>
                        <h3>Video(s):</h3>
                        <HtmlEmbed {...fields.videos} />
                    </section>
                ) : null}

                {fields.articles?.value ? (
                    <section>
                        <h3>Related Articles:</h3>
                        <HtmlEmbed {...fields.articles} />
                    </section>
                ) : null}

                <CoastContentShowBumperMusic show={resolvedPayload.right} />
            </article>

            <ShareButtons
                url={url}
                title={content.summary.title}
                desc={content.summary.description}
                setStyles={false}
            />

            {/* TODO: Previous-next */}
        </Container>
    );
});

export default CoastContentShow;
